/**
 * BatchManager for optimizing API calls
 * Collects like/save operations and sends them in batches to reduce server load
 */

import { fetchPostApi } from './fetch';
import performanceMonitor from './performanceMonitor';

class BatchManager {
    constructor() {
        this.pendingOperations = new Map();
        this.batchTimeout = null;
        this.batchDelay = 500; // 500ms delay before sending batch
        this.maxBatchSize = 10; // Max operations per batch
    }

    /**
     * Add an operation to the batch queue
     * @param {string} type - 'like' or 'save'
     * @param {string} newsId - News item ID
     * @param {string} userId - User ID
     * @param {boolean} action - true for like/save, false for unlike/unsave
     * @param {Function} onSuccess - Success callback
     * @param {Function} onError - Error callback
     */
    addOperation(type, newsId, userId, action, onSuccess, onError) {
        const operationKey = `${type}_${newsId}`;
        
        // Store the operation (overwrites previous operation for same news item)
        this.pendingOperations.set(operationKey, {
            type,
            newsId,
            userId,
            action,
            onSuccess,
            onError,
            timestamp: Date.now()
        });

        // Clear existing timeout and set new one
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
        }

        // Send batch immediately if we hit max size, otherwise wait for delay
        if (this.pendingOperations.size >= this.maxBatchSize) {
            this.processBatch();
        } else {
            this.batchTimeout = setTimeout(() => {
                this.processBatch();
            }, this.batchDelay);
        }
    }

    /**
     * Process the current batch of operations
     */
    async processBatch() {
        if (this.pendingOperations.size === 0) return;

        // Clear timeout
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }

        // Convert operations to arrays for processing
        const operations = Array.from(this.pendingOperations.values());
        const likes = operations.filter(op => op.type === 'like');
        const saves = operations.filter(op => op.type === 'save');

        // Clear pending operations
        this.pendingOperations.clear();

        // Process likes batch
        if (likes.length > 0) {
            await this.processBatchType('like', likes);
        }

        // Process saves batch
        if (saves.length > 0) {
            await this.processBatchType('save', saves);
        }
    }

    /**
     * Process a specific type of batch operation
     * @param {string} type - 'like' or 'save'
     * @param {Array} operations - Array of operations
     */
    async processBatchType(type, operations) {
        try {
            // Check if we have batch endpoints available
            const hasBatchEndpoint = await this.checkBatchEndpoint(type);
            
            if (hasBatchEndpoint) {
                // Use batch endpoint
                await this.sendBatchRequest(type, operations);
            } else {
                // Fallback to individual requests
                await this.sendIndividualRequests(type, operations);
            }
        } catch (error) {
            console.error(`Error processing ${type} batch:`, error);
            
            // Try individual requests as fallback
            await this.sendIndividualRequests(type, operations);
        }
    }

    /**
     * Check if batch endpoint is available
     * @param {string} type - 'like' or 'save'
     */
    async checkBatchEndpoint(type) {
        // For now, we'll use individual requests since batch endpoints may not exist
        // This can be easily switched when batch endpoints are implemented
        return false;
    }

    /**
     * Send batch request to server
     * @param {string} type - 'like' or 'save'
     * @param {Array} operations - Array of operations
     */
    async sendBatchRequest(type, operations) {
        const batchData = operations.map(op => ({
            newsId: op.newsId,
            userId: op.userId,
            action: op.action
        }));

        const endpoint = type === 'like' ? '/api/likes/news/batch' : '/api/news/save/batch';
        
        // Start performance monitoring for batch
        const operationId = performanceMonitor.startOperation('batch_call', `${type}_batch_${operations.length}`);
        performanceMonitor.incrementApiCall('batch');
        
        try {
            const response = await fetchPostApi(endpoint, { operations: batchData });
            
            if (response.success) {
                performanceMonitor.endOperation(operationId, true);
                // Call success callbacks
                operations.forEach(op => {
                    if (op.onSuccess) op.onSuccess();
                });
            } else {
                performanceMonitor.endOperation(operationId, false);
                throw new Error(`Batch ${type} request failed: ${response.message}`);
            }
        } catch (error) {
            performanceMonitor.endOperation(operationId, false);
            // Call error callbacks
            operations.forEach(op => {
                if (op.onError) op.onError(error);
            });
            throw error;
        }
    }

    /**
     * Send individual requests (fallback or default)
     * @param {string} type - 'like' or 'save'
     * @param {Array} operations - Array of operations
     */
    async sendIndividualRequests(type, operations) {
        const promises = operations.map(async (operation) => {
            try {
                await this.sendIndividualRequest(operation);
                if (operation.onSuccess) operation.onSuccess();
            } catch (error) {
                console.error(`Individual ${type} request failed:`, error);
                if (operation.onError) operation.onError(error);
            }
        });

        // Wait for all requests to complete
        await Promise.allSettled(promises);
    }

    /**
     * Send individual API request
     * @param {Object} operation - The operation to send
     */
    async sendIndividualRequest(operation) {
        const { type, newsId, action } = operation;
        
        // Start performance monitoring
        const operationId = performanceMonitor.startOperation('api_call', `${type}_${newsId}`);
        performanceMonitor.incrementApiCall('individual');
        
        let endpoint;
        
        if (type === 'like') {
            endpoint = action 
                ? `/api/likes/news_likes/${newsId}/like`
                : `/api/likes/news_likes/${newsId}/unlike`;
        } else if (type === 'save') {
            endpoint = `/api/news/${newsId}/add/saved${!action ? '?delete=true' : ''}`;
        }

        try {
            const response = await fetchPostApi(endpoint, {});
            
            if (!response.success) {
                performanceMonitor.endOperation(operationId, false);
                throw new Error(`${type} request failed: ${response.message}`);
            }
            
            performanceMonitor.endOperation(operationId, true);
            return response;
        } catch (error) {
            performanceMonitor.endOperation(operationId, false);
            throw error;
        }
    }

    /**
     * Force process any pending operations (useful for page unload)
     */
    flush() {
        if (this.pendingOperations.size > 0) {
            this.processBatch();
        }
    }

    /**
     * Get the number of pending operations
     */
    getPendingCount() {
        return this.pendingOperations.size;
    }

    /**
     * Clear all pending operations
     */
    clear() {
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        this.pendingOperations.clear();
    }
}

// Create singleton instance
const batchManager = new BatchManager();

// Flush batch on page unload
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        batchManager.flush();
    });
}

export default batchManager;